import { AUTO_FOCUS_GRACE, MINUTE } from './constants';
import type { Phase, RunState, Settings } from './types';

export const phaseMs = (settings: Settings, phase: Phase): number => settings[phase] * MINUTE;

export function idleRun(settings: Settings, phase: Phase = 'focus', round = 1): RunState {
  const total = phaseMs(settings, phase);
  return { phase, round, total, remaining: total, endAt: 0, running: false, interruptions: 0 };
}

/** Started (running or paused part-way) as opposed to sitting at full length. */
export const inProgress = (run: RunState): boolean => run.running || run.remaining < run.total;

export const remainingAt = (run: RunState, now: number): number =>
  run.running ? Math.max(0, run.endAt - now) : run.remaining;

export const fractionLeft = (run: RunState, now: number): number =>
  run.total > 0 ? Math.min(1, Math.max(0, remainingAt(run, now) / run.total)) : 0;

export function start(run: RunState, now: number): RunState {
  if (run.running) return run;
  const remaining = run.remaining > 0 ? run.remaining : run.total;
  return { ...run, remaining, endAt: now + remaining, running: true };
}

export function pause(run: RunState, now: number): RunState {
  if (!run.running) return run;
  return { ...run, remaining: Math.max(0, run.endAt - now), endAt: 0, running: false };
}

/**
 * A running timer can never end more than one full length from now. Guards against the system
 * clock moving backwards and hand-edited backups, which would otherwise show e.g. 85:00 of 25:00.
 */
export function capEndAt<R extends { running: boolean; endAt: number; total: number }>(r: R, now: number): R {
  return r.running && r.endAt > now + r.total ? { ...r, endAt: now + r.total } : r;
}

/** Back to the start of the current phase, picking up the current settings. */
export function reset(run: RunState, settings: Settings): RunState {
  return idleRun(settings, run.phase, run.round);
}

export function nextPhase(run: RunState, settings: Settings): { phase: Phase; round: number } {
  if (run.phase === 'focus')
    return { phase: run.round >= settings.interval ? 'long' : 'short', round: run.round };
  if (run.phase === 'short') return { phase: 'focus', round: run.round + 1 };
  return { phase: 'focus', round: 1 };
}

/** If the user changes a duration, an untouched phase picks it up immediately. */
export function applySettings(run: RunState, settings: Settings): RunState {
  if (inProgress(run)) return run;
  return idleRun(settings, run.phase, run.round);
}

export interface PhaseCompletion {
  phase: Phase;
  round: number;
  /** Actual length of the completed phase in minutes. */
  minutes: number;
  endedAt: number;
  interruptions: number;
  natural: boolean;
}

/** Ends the current phase and moves to the next one (idle, or running when auto-start applies). */
export function complete(
  run: RunState,
  settings: Settings,
  opts: { endedAt: number; natural: boolean; now: number },
): { run: RunState; completion: PhaseCompletion } {
  const completion: PhaseCompletion = {
    phase: run.phase,
    round: run.round,
    minutes: Math.round(run.total / MINUTE),
    endedAt: opts.endedAt,
    interruptions: run.interruptions,
    natural: opts.natural,
  };
  const n = nextPhase(run, settings);
  let next = idleRun(settings, n.phase, n.round);
  if (opts.natural) {
    const auto = run.phase === 'focus' ? settings.autoBreak : settings.autoFocus;
    // Never fabricate focus sessions while the user was away: auto-start focus only if fresh.
    const fresh = opts.now - opts.endedAt <= AUTO_FOCUS_GRACE;
    if (auto && (n.phase !== 'focus' || fresh)) {
      next = { ...next, running: true, endAt: opts.endedAt + next.total };
    }
  }
  return { run: next, completion };
}

/**
 * Processes every phase that ended at or before `now` (e.g. after sleep or a closed tab).
 * Each completion is timestamped with the moment it actually ended.
 */
export function advance(
  run: RunState,
  settings: Settings,
  now: number,
): { run: RunState; completions: PhaseCompletion[] } {
  const completions: PhaseCompletion[] = [];
  let cur = run;
  for (let guard = 0; cur.running && cur.endAt <= now && guard < 500; guard++) {
    const r = complete(cur, settings, { endedAt: cur.endAt, natural: true, now });
    completions.push(r.completion);
    cur = r.run;
  }
  return { run: cur, completions };
}
